(ns dre.pages.frontpage.topic-search.views.topic-form
  (:require
   ["antd" :refer [Switch Typography]]
   [clojure.string]
   [dre.components.loading-spinner :refer [loading-spinner]]
   [dre.components.searchbar.views :refer [searchbar]]
   [dre.components.searchbar.events :as searchbar-events]
   [dre.components.searchbar.subs :as searchbar-subs]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.i18n.views :refer [tr]]
   [dre.pages.frontpage.topic-search.events :as events]
   [dre.page-util :as page-util]
   [dre.utils.bem :as bem]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :topic-search-form)

(defmulti form :form-type)

(defn is-checkbox-selected
  [pills label]
  (or (some #(= % label) pills)
      (some #(and
              (= (:pill/query %) (:pill/query label))
              (= (:pill/type %) (:pill/type label))) pills)))

(defn- topic-searchbar
  [_ _ _ _]
  (let [ref (atom nil)]
    (r/create-class
     {:component-did-mount #()
      :reagent-render
      (fn [db-root-path selected-topic props searchbar-ref]
        (let [search-query (rf/subscribe [::searchbar-subs/search-query db-root-path])
              suggestions (rf/subscribe [::searchbar-subs/suggestions db-root-path])
              on-type (page-util/debounce2 #(rf/dispatch-sync [::events/fetch-suggestions db-root-path %]) 125)
              on-submit (constantly nil)
              on-change (fn [search-query] [::events/on-pills-change search-query])
              clear-query #(do
                             (rf/dispatch [::searchbar-events/clear-query db-root-path {:on-change on-change}])
                             (rf/dispatch [::searchbar-events/clear-counts db-root-path]))
              current-operator (or @(rf/subscribe [::events/get-form-operator selected-topic (:form-id props)]) (:operator props))
              show-hint? (fn [{:keys [pill-count]}]
                           (>= pill-count 2))
              hint (r/atom [:span
                            {:class (bem/be component-name :form-operator)}
                            [:> Switch {:checked (if (= current-operator :and)
                                                   true
                                                   false)
                                        :checkedChildren "AND"
                                        :unCheckedChildren "OR"
                                        :size "small"
                                        :on-change (fn [checked event]
                                                     (let [val (if checked :and :or)]
                                                       (rf/dispatch [::events/set-form-operator selected-topic (:form-id props) val])))}]])]
          [searchbar {:db-root-path db-root-path
                      :query search-query
                      :suggestions suggestions
                      :hint hint
                      :placeholder [tr "Type then select terms to search on."]
                      :on-type on-type
                      :on-submit on-submit
                      :on-change on-change
                      :clear-query clear-query
                      :show-buttons? false
                      :show-footer? false
                      :show-suffix-icon? false
                      :show-hint? show-hint?
                      :searchbar-ref searchbar-ref
                      :suggestion-tooltip-placement "right"}]))})))

(defn- example
  [_ _]
  (let [ellipsis (r/atom false)]
    (r/create-class
     {:component-did-mount #()
      :reagent-render
      (fn [current-pills ex]
        [tooltip-wrapper (when @ellipsis (events/get-pill-label ex))
         [:span
          {:class-name (when (is-checkbox-selected current-pills ex) "selected")}
          [:> Typography.Text
           {:ellipsis {:onEllipsis (fn [e] (reset! ellipsis e))}}
           (events/get-pill-label ex)]]])})))

(defmethod form :common
  [props]
    (let [searchbar-ref (r/atom nil)
          dom-ref (atom nil)
          height-of-3-line-searchbar 113 ; we use this value because we need to change the height of container box after searchbar exceeds this height
          ;; this ResizeObserver observes the height of searchbar to calculate height of container box
          resize-observer (js/ResizeObserver.
                           (let [class-name (str (bem/be component-name :form-searchbar) "--expanded")]
                             #(when-let [x (->> %
                                                (filter (fn [x] (= (.-target x) @dom-ref)))
                                                first)]
                                (if (> (.. x -contentRect -height) height-of-3-line-searchbar)
                                  (-> @dom-ref .-classList (.add class-name))
                                  (-> @dom-ref .-classList (.remove class-name))))))]
      (r/create-class
       {:component-did-mount
        (fn [_this]
          (.observe resize-observer @dom-ref)
          (when (= (:idx props) 0)
            (.click @searchbar-ref)))
        :reagent-render
        (fn [props]
          (let [selected-topic @(rf/subscribe [::events/selected-topic])
                current-pills @(rf/subscribe [::events/get-form-pills selected-topic (:form-id props)])
                examples @(rf/subscribe [::events/get-form-examples selected-topic (:form-id props)])
                examples-loading? @(rf/subscribe [::events/is-form-examples-loading? selected-topic (:form-id props)])
                examples (concat current-pills (remove (fn [e]
                                                         (some #(and
                                                                 (or 
                                                                  (= (:pill/query %) (:pill/query e))
                                                                  (some #{(:pill/query %)} (:pill/other-senses e))
                                                                  (some #{(:pill/query e)} (:pill/other-senses %)))
                                                                 (= (if (= (:pill/type %) :senses)
                                                                      :term
                                                                      (:pill/type %)) (:pill/type e))) current-pills))
                                                       examples))]
            [:div
             {:class component-name}
             [:div
              {:class (bem/be component-name :form)}
              [:div
               {:class (bem/be component-name :form-title)}
               (:title props)
               (when (:required? props)
                 "*")]
              [:div
               {:ref #(reset! dom-ref %)
                :class (bem/be component-name :form-searchbar)}
               [topic-searchbar (vec (concat events/db-path [:ui selected-topic (:form-id props)])) selected-topic props searchbar-ref]]
              [:div.examples "Examples"]

              (if examples-loading?
                [loading-spinner]
                [:div.examples-items
                 (doall
                  (map-indexed (fn [idx ex]
                                 ^{:key idx}
                                 [:div.examples-item
                                  {:on-click #(rf/dispatch [::events/set-form-pills selected-topic (:form-id props) ex])}
                                  [:input {:type "checkbox"
                                           :checked (if (is-checkbox-selected current-pills ex) true false)}]
                                  [example current-pills ex]])
                               examples))])]]))})))

(defn view
  [idx props]
  [form (assoc props :idx idx)])

